import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent, DialogConfig } from '@vsolv/vectors-ui/dialog';
import {
  EMAIL_TEMPLATE_TOKENS,
  EmailTemplate,
  EmailTemplateToken,
  EmailTemplatesService,
} from '../../pages/email-templates/email-templates.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-settings-edit-email-template-dialog',
  templateUrl: './edit-email-template.dialog.html',
  standalone: false,
})
export class EditEmailTemplateDialog implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Input() dialogConfig?: DialogConfig;

  saving = false;
  copiedToken: string | null = null;
  template: EmailTemplate | null = null;

  private copiedTokenTimeout: ReturnType<typeof setTimeout> | null = null;

  form = this.formBuilder.group({
    enabled: [true, Validators.required],
    templateLabel: [''],
    subject: [''],
    byline: [''],
    content: [''],
  });

  resetFormValue = {
    enabled: true,
    templateLabel: '',
    subject: '',
    byline: '',
    content: '',
  };

  constructor(
    private formBuilder: FormBuilder,
    private emailTemplatesSvc: EmailTemplatesService,
    private toastSvc: ToastService
  ) {}

  get tokens(): EmailTemplateToken[] {
    return this.template ? (EMAIL_TEMPLATE_TOKENS[this.template.type] ?? []) : [];
  }

  @Input() set emailTemplate(template: EmailTemplate | null) {
    if (!template) return;

    this.template = template;
    this.resetFormValue = {
      enabled: template.enabled,
      templateLabel: template.templateLabel ?? '',
      subject: template.subject ?? '',
      byline: template.byline ?? '',
      content: template.content ?? '',
    };
    this.resetForm();
  }

  copyToken(token: string) {
    if (this.copiedTokenTimeout) clearTimeout(this.copiedTokenTimeout);

    this.copiedToken = token;
    this.toastSvc.show({ type: 'success', title: 'Copied!', text: `${token} copied to clipboard.` });
    this.copiedTokenTimeout = setTimeout(() => (this.copiedToken = null), 2000);
  }

  ngOnDestroy() {
    if (this.copiedTokenTimeout) clearTimeout(this.copiedTokenTimeout);
  }

  resetForm() {
    this.form.reset(this.resetFormValue);
    this.form.markAsPristine();
  }

  open(template: EmailTemplate) {
    // Apply before open so header/editor exist when the overlay attaches.
    this.emailTemplate = template;
    this.dialog.open();
    // Dialog.open() only flushes the overlay view; refresh projected form content now.
    this.cdr.detectChanges();
  }

  close() {
    this.dialog.close();
  }

  cancel() {
    this.resetForm();
    this.close();
  }

  async submit() {
    if (!this.template) return;

    this.saving = true;

    const updated = await this.emailTemplatesSvc
      .updateTemplate(this.template.id, {
        enabled: this.form.controls.enabled.value ?? undefined,
        templateLabel: this.form.controls.templateLabel.value ?? undefined,
        subject: this.form.controls.subject.value ?? undefined,
        byline: this.form.controls.byline.value ?? undefined,
        content: this.form.controls.content.value ?? undefined,
      })
      .catch(() => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: 'Unable to update email template.',
        });
        return null;
      });

    if (updated?.id) {
      this.toastSvc.show({
        type: 'success',
        title: 'Updated email template',
        text: `<strong>${this.template.type}</strong> has been successfully updated.`,
      });
      this.close();
    }

    this.saving = false;
  }
}
